/*
 * Theme cart drawer runtime — window.SourCart.
 *
 * The drawer's HTML is Liquid (sections/cart-drawer.liquid). Every cart
 * mutation asks Shopify to render that section in the same request
 * (`sections: 'cart-drawer'`) and swaps the body + footer in place; the
 * open state and the slide transition live on the root and survive.
 *
 * It also owns the things Monster Cart used to do for the theme:
 *  - the header cart icon opens it (no-JS still navigates to /cart);
 *  - every cart/add form on the site is intercepted at the capture phase
 *    (product page, collection cards, quickview) so the add happens over
 *    fetch and the drawer opens — main.js binds its own handlers on the
 *    form elements, so a document-level capture listener wins;
 *  - main.js's decorated-button spinner is cleared after the add;
 *  - the header badge is updated from the cart count;
 *  - `window.monster_refresh` / `window.MonsterCart.open` shims keep any
 *    untouched caller working.
 *
 * Events on document: cart:updated {cart, source, action}, cart:opened,
 * cart:closed, cart:error.
 *
 * On /cart the same section is the page (data-cart-page on the root, see
 * sections/cart-drawer.liquid): it is always "open", never closes, traps
 * no focus and locks no scroll; everything else — edits, the backpack,
 * the offers, re-rendering from the root path's section — is the same.
 */
(() => {
  if (window.SourCart) return;

  const SECTION = 'cart-drawer';
  const ROOT_PATH = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const UNDO_MS = 30000;
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const root = () => document.getElementById('cart-drawer');
  const isPage = () => {
    const el = root();
    return !!el && el.hasAttribute('data-cart-page');
  };
  const reducedMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} }));

  const jsonHeaders = { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' };

  const parseSection = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.getElementById('cart-drawer');
  };

  // A hung request must fail, not wedge the queue: 20s is generous for a
  // cart call and short enough that a shopper sees the error notice.
  const REQUEST_MS = 20000;
  const timeoutSignal = () => (typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(REQUEST_MS) : undefined);
  const fetchCart = () => fetch(ROOT_PATH + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' }, signal: timeoutSignal() }).then((r) => r.json());
  const fetchSection = () =>
    fetch(ROOT_PATH + '?sections=' + SECTION, { credentials: 'same-origin', signal: timeoutSignal() })
      .then((r) => r.json())
      .then((j) => j[SECTION]);

  const setBadge = (count) => {
    const badge = document.querySelector('#header-cart [data-cart-count]');
    if (!badge) return;
    const prev = parseInt(badge.textContent, 10) || 0;
    badge.textContent = String(count);
    if (count > 0) badge.removeAttribute('hidden');
    else badge.setAttribute('hidden', '');
    const label = badge.getAttribute('data-count-label');
    if (label) {
      const link = badge.closest('a') || document.getElementById('header-cart');
      if (link) link.setAttribute('aria-label', label.replace('__count__', count));
    }
    if (count > prev) {
      badge.setAttribute('data-bump', '');
      setTimeout(() => badge.removeAttribute('data-bump'), 400);
    }
  };

  const clearSpinners = () => {
    document.querySelectorAll('.decorated-button--loading, .decorated-button[aria-busy="true"]').forEach((b) => {
      b.classList.remove('decorated-button--loading');
      b.removeAttribute('aria-busy');
    });
  };

  class Drawer {
    constructor() {
      this.cart = null;
      this.opener = null;
      this.closeTimer = null;
      this.queue = Promise.resolve();
      this.seq = 0;
      this.pending = new Map(); // key -> timer for coalesced qty edits
      this.undo = null;
      this.undoTimer = null;
      this.bind();
      this.mountPack();
      this.watchWallets();
      this.fitLayout();
      window.addEventListener('resize', () => this.fitLayout(), { passive: true });
      this.installShims();
    }

    /* ---------- the wallet buttons ---------- */

    /**
     * Shopify's accelerated checkout widget (shopify-accelerated-checkout-cart,
     * a closed shadow root) stacks the wallets one full-width row each (the
     * wrapper's additional-checkout-buttons--vertical), but it keeps a row for
     * a wallet it hides (unsupported on the device, or not loaded yet) and can
     * park a late one (PayPal's SDK iframe) under its neighbour — a hole
     * between two buttons, or one behind another. The buttons are light-DOM
     * children of the host, so this pass re-stacks the ones that rendered, in
     * order, with the widget's row gap, and pulls the footer up by the rows
     * that stayed empty. Runs after every render and whenever a wallet
     * appears, resizes or goes.
     */
    fitWallets() {
      const host = document.querySelector('.cart-drawer__dynamic shopify-accelerated-checkout-cart');
      if (!host) return;
      const wrap = host.closest('.cart-drawer__dynamic');
      const gap = parseFloat(getComputedStyle(wrap).getPropertyValue('--shopify-accelerated-checkout-row-gap')) || 8;
      const vertical = wrap.classList.contains('additional-checkout-buttons--vertical');
      const kids = Array.from(host.children);
      kids.forEach((k) => {
        k.style.transform = '';
        k.style.width = '';
      });
      wrap.style.marginBottom = '';
      wrap.style.minHeight = '';
      const hostRect = host.getBoundingClientRect();
      if (!hostRect.height || !kids.length) return;
      // A wallet the widget parked off-screen or collapsed to nothing has not
      // rendered. In the stack a box under 24px is a collapsed SDK iframe
      // painting as a sliver over its neighbour: out of the stack and out of
      // sight until it grows, when the ResizeObserver brings it back. Side
      // by side the boxes are only ~20px tall by design (the buttons draw
      // taller), so only width and position count there.
      const shown = kids
        .map((k) => ({ k, r: k.getBoundingClientRect() }))
        .filter(({ k, r }) => {
          const ok = r.width > 0 && r.bottom > hostRect.top - 1 && (!vertical || r.height >= 24);
          if (getComputedStyle(k).display !== 'none') k.style.visibility = ok ? '' : 'hidden';
          return ok;
        })
        .sort((a, b) => a.r.top - b.r.top);
      if (!shown.length) return;
      if (vertical) {
        let y = 0;
        shown.forEach(({ k, r }) => {
          const dy = Math.round(y - (r.top - hostRect.top));
          if (dy) k.style.transform = 'translateY(' + dy + 'px)';
          y += r.height + gap;
        });
        // Only the rows the widget reserved for wallets that never showed are
        // taken back — never the terms line it prints under the buttons.
        const empty = kids.length - shown.length;
        if (empty > 0) wrap.style.marginBottom = -Math.round(empty * (shown[0].r.height + gap)) + 'px';
      } else {
        // Side by side: the widget made one equal column per configured
        // wallet; the rendered ones share the row instead, each as wide as
        // the row allows (its button fills the box: inline-size 100%).
        shown.sort((a, b) => a.r.left - b.r.left);
        const n = shown.length;
        const w = Math.floor((hostRect.width - gap * (n - 1)) / n);
        // The widget's row is shorter than the buttons it draws: hold their height in the footer.
        const size = parseFloat(getComputedStyle(wrap).getPropertyValue('--shopify-accelerated-checkout-button-block-size')) || 50;
        wrap.style.minHeight = size + 'px';
        shown.forEach(({ k, r }, i) => {
          k.style.width = w + 'px';
          const dx = Math.round(i * (w + gap) - (r.left - hostRect.left));
          if (dx) k.style.transform = 'translateX(' + dx + 'px)';
        });
      }
      this.walletSig = shown.map(({ k }) => k.tagName + ':' + k.style.transform + ':' + k.style.width).join('|');
      this.fitLayout();
    }

    /**
     * The footer is never capped (the wallets must show whole), so on a
     * short phone a tall footer crushed the bag list: a Squire's Box plus
     * bags (subscription terms, four wallet rows) left 42px of list at
     * 375x667 and pushed the last wallet 24px off a 320x640 screen. When the
     * footer would leave the list under LIST_MIN (about two rows of bags),
     * the drawer scrolls as one page instead (is-stacked: bags, then totals,
     * Checkout and wallets); the header stays on top. The footer's height
     * doesn't depend on the mode, so the switch can't flip back and forth.
     */
    fitLayout() {
      const LIST_MIN = 240;
      const panel = document.querySelector('.cart-drawer:not(.cart-drawer--page) .cart-drawer__panel');
      if (!panel) return;
      const footer = panel.querySelector('.cart-drawer__footer');
      const header = panel.querySelector('.cart-drawer__header');
      const room = panel.clientHeight - (header ? header.offsetHeight : 0);
      const tall = !!footer && footer.offsetHeight > 0 && room > 0 && room - footer.offsetHeight < LIST_MIN;
      panel.classList.toggle('is-stacked', tall);
    }

    watchWallets() {
      const run = () => {
        if (this.walletRaf) return;
        this.walletRaf = requestAnimationFrame(() => {
          this.walletRaf = 0;
          this.fitWallets();
        });
      };
      const arm = () => {
        const host = document.querySelector('.cart-drawer__dynamic shopify-accelerated-checkout-cart');
        if (!host || host.__walletWatch) return;
        host.__walletWatch = true;
        const ro = window.ResizeObserver ? new ResizeObserver(run) : null;
        if (ro) {
          ro.observe(host);
          Array.from(host.children).forEach((k) => ro.observe(k));
        }
        new MutationObserver((muts) => {
          muts.forEach((m) => m.addedNodes.forEach((n) => n.nodeType === 1 && ro && ro.observe(n)));
          // A wallet the widget moves (a late one lands in its row without
          // changing size) shows up here as a style change that is not ours.
          const mine = muts.every((m) => m.type === 'attributes' && m.target.parentElement === host && (this.walletSig || '').indexOf(m.target.tagName + ':' + m.target.style.transform + ':' + m.target.style.width) !== -1);
          if (!mine) run();
        }).observe(host, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden', 'disabled'] });
        run();
      };
      this.armWallets = arm;
      arm();
      window.addEventListener('resize', run, { passive: true });
      window.addEventListener('load', run);
      // The wallets' SDKs land on their own clocks; late passes catch a slow
      // one, and a light poll while the drawer is open catches a move the
      // observers cannot see (a box repositioned inside the widget's grid).
      setTimeout(run, 1500);
      setTimeout(run, 4000);
      setInterval(() => {
        if (this.isOpen) run();
      }, 700);
    }

    /* ---------- open / close ---------- */

    get isOpen() {
      const el = root();
      return !!el && el.classList.contains('is-open');
    }

    /**
     * Keep the page still behind the drawer. overflow:hidden is enough on
     * desktop; touch browsers need the body pinned, with the scroll offset
     * kept so unlocking lands the shopper where they were.
     */
    lockScroll() {
      if (this.scrollLock != null) return;
      this.scrollLock = window.scrollY || 0;
      document.body.classList.add('cart-drawer-open');
      if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
        document.body.style.top = -this.scrollLock + 'px';
        document.body.classList.add('cart-drawer-fixed');
      }
    }
    unlockScroll() {
      if (this.scrollLock == null) return;
      const y = this.scrollLock;
      this.scrollLock = null;
      const pinned = document.body.classList.contains('cart-drawer-fixed');
      document.body.classList.remove('cart-drawer-open', 'cart-drawer-fixed');
      document.body.style.top = '';
      if (pinned) window.scrollTo(0, y);
    }

    open(opts) {
      const el = root();
      if (!el) {
        window.location.href = ROOT_PATH + 'cart';
        return;
      }
      const o = opts || {};
      if (!this.isOpen) {
        clearTimeout(this.closeTimer);
        this.opener = document.activeElement;
        el.hidden = false;
        el.removeAttribute('aria-hidden');
        this.lockScroll();
        // Force a style flush so the transform transition runs from the
        // off-screen position; a timeout rather than rAF so it also fires
        // when the tab isn't painting (background tab, previews).
        void el.offsetWidth;
        setTimeout(() => {
          el.classList.add('is-open');
          const panel = el.querySelector('.cart-drawer__panel');
          if (panel) panel.focus({ preventScroll: true });
        }, 16);
        const link = document.getElementById('header-cart');
        if (link) link.setAttribute('aria-expanded', 'true');
        emit('cart:opened');
      }
      if (o.refresh !== false) this.refresh({ source: 'open', quiet: true });
    }

    close() {
      const el = root();
      if (!el || !this.isOpen || isPage()) return;
      el.classList.remove('is-open');
      this.unlockScroll();
      const link = document.getElementById('header-cart');
      if (link) link.setAttribute('aria-expanded', 'false');
      const finish = () => {
        el.hidden = true;
        el.setAttribute('aria-hidden', 'true');
        if (this.opener && typeof this.opener.focus === 'function') {
          try {
            this.opener.focus({ preventScroll: true });
          } catch (e) {}
        }
        this.opener = null;
      };
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(finish, reducedMotion() ? 0 : 700);
      emit('cart:closed');
    }

    toggle() {
      if (this.isOpen) this.close();
      else this.open();
    }

    /* ---------- rendering ---------- */

    render(html, opts) {
      const el = root();
      if (!el || !html) return;
      const fresh = parseSection(html);
      if (!fresh) return;
      const o = opts || {};
      const body = el.querySelector('[data-cart-body]');
      const footer = el.querySelector('[data-cart-footer]');
      const scrollTop = body ? body.scrollTop : 0;
      // Keep the element the shopper is working in where it is on screen:
      // a powder toggle inserts a line above the strip, which would shove
      // the strip down under the finger mid-tap.
      const anchorSel = this.anchor;
      this.anchor = null;
      const anchorEl = anchorSel && el.querySelector(anchorSel);
      const anchorTop = anchorEl ? anchorEl.getBoundingClientRect().top : null;
      const active = document.activeElement;
      let focusKey = null;
      if (active && el.contains(active)) {
        const slot = active.hasAttribute('data-inv-btn') ? active.closest('[data-inv-slot]') : null;
        focusKey = slot
          ? { action: 'slot', key: slot.getAttribute('data-key'), index: slot.getAttribute('data-index') }
          : { action: active.getAttribute('data-cart-qty') != null ? 'qty' + active.getAttribute('data-cart-qty') : active.getAttribute('data-cart-remove') != null ? 'remove' : active.getAttribute('data-cart-sourup') != null ? 'sourup' : null, key: active.getAttribute('data-key') || active.getAttribute('data-variant') };
      }
      // The backpack's menu and card act by line key on a fresh grid: close them before the swap.
      if (window.SourInventory) {
        window.SourInventory.menu.close();
        window.SourInventory.card.close();
      }
      const oldMeter = Number(el.dataset.meter) || 0;
      const oldFill = el.dataset.shipFill;
      const wasFree = el.dataset.shipsFree === 'true';
      // The discount box is replaced by the fresh markup like everything
      // else in the footer, so a half-typed code would vanish under any
      // other edit (a quantity bump, an add-on). Keep what was typed.
      const oldCode = el.querySelector('[data-cart-code-input]');
      const codeWasOpen = el.querySelector('[data-cart-code]')?.open;
      const codeState = oldCode ? { value: oldCode.value, focused: document.activeElement === oldCode } : null;

      ['data-item-count', 'data-bag-count', 'data-meter', 'data-ship-fill', 'data-ships-free'].forEach((a) => {
        if (fresh.hasAttribute(a)) el.setAttribute(a, fresh.getAttribute(a));
      });
      // The backpack's slots survive the swap: a fresh grid would re-decode
      // every bag's art and replay every pop-in on each edit (the whole box
      // twitched when one bag left). Slots that stay — same line key, same
      // ordinal within it — keep their DOM node and take the new markup's
      // attributes; only what changed is new.
      const keep = new Map();
      const ordinal = new Map();
      if (body) {
        body.querySelectorAll('[data-cart-pack] [data-inv-slot][data-key]').forEach((li) => {
          const k = li.getAttribute('data-key');
          const n = ordinal.get(k) || 0;
          ordinal.set(k, n + 1);
          if (!li.classList.contains('is-removing')) keep.set(k + '#' + n, li);
        });
      }
      // The dynamic checkout buttons (Shop Pay / PayPal) are iframes their
      // SDKs build once; a fresh copy on every edit re-initialised them (28
      // mutations, Storefront API calls, a layout jump under the total). Keep
      // the live container when the new markup has one too.
      const oldDynamic = footer && footer.querySelector('.cart-drawer__dynamic');
      const freshBody = fresh.querySelector('[data-cart-body]');
      const freshFooter = fresh.querySelector('[data-cart-footer]');
      const newDynamic = freshFooter && freshFooter.querySelector('.cart-drawer__dynamic');
      let keptDynamic = false;
      // The live widget is kept only while the server still describes the
      // same widget: a subscription entering or leaving the cart changes the
      // wallet set (PayPal cannot pay a subscription) and the terms line, and
      // the widget reads those once, at initialisation — so then it is
      // rebuilt fresh (Shopify.PaymentButton.init() below).
      const widgetKey = (wrap) => {
        const host = wrap && wrap.querySelector('shopify-accelerated-checkout-cart');
        if (!host) return null;
        return ['wallet-configs', 'has-selling-plan', 'requires-shipping', 'buyer-currency', 'buyer-country', 'buyer-locale']
          .map((a) => a + '=' + (host.getAttribute(a) === null ? '' : host.getAttribute(a)))
          .join('|');
      };
      const sameWidget = oldDynamic && newDynamic && widgetKey(oldDynamic) !== null && widgetKey(oldDynamic) === widgetKey(newDynamic);
      if (body && freshBody) body.replaceChildren(...freshBody.childNodes);
      if (footer && freshFooter) {
        if (sameWidget) {
          // The live container must never leave the DOM: moving it (even
          // straight back in) re-connects Shopify's wallet elements and
          // PayPal's SDK renders a second button into the same element —
          // two half-width PayPal buttons a row down, behind G Pay. So the
          // footer is rebuilt around it: every ancestor between the footer
          // and the container keeps its element, its siblings are the fresh
          // markup, and the container itself is untouched.
          const graft = (oldParent, freshParent) => {
            const oldPath = Array.from(oldParent.children).find((c) => c === oldDynamic || c.contains(oldDynamic));
            const freshPath = Array.from(freshParent.children).find((c) => c === newDynamic || c.contains(newDynamic));
            if (!oldPath || !freshPath) {
              oldParent.replaceChildren(...freshParent.childNodes);
              return;
            }
            const before = [];
            const after = [];
            let seen = false;
            Array.from(freshParent.childNodes).forEach((n) => {
              if (n === freshPath) seen = true;
              else (seen ? after : before).push(n);
            });
            Array.from(oldParent.childNodes).forEach((n) => {
              if (n !== oldPath) n.remove();
            });
            before.forEach((n) => oldParent.insertBefore(n, oldPath));
            after.forEach((n) => oldParent.appendChild(n));
            if (oldPath !== oldDynamic) {
              Array.from(oldPath.attributes).forEach((a) => {
                if (!freshPath.hasAttribute(a.name)) oldPath.removeAttribute(a.name);
              });
              Array.from(freshPath.attributes).forEach((a) => oldPath.setAttribute(a.name, a.value));
              graft(oldPath, freshPath);
            }
          };
          graft(footer, freshFooter);
          keptDynamic = true;
        } else {
          footer.replaceChildren(...freshFooter.childNodes);
        }
      }
      if (body && keep.size) {
        ordinal.clear();
        body.querySelectorAll('[data-cart-pack] [data-inv-slot][data-key]').forEach((li) => {
          const k = li.getAttribute('data-key');
          const n = ordinal.get(k) || 0;
          ordinal.set(k, n + 1);
          const old = keep.get(k + '#' + n);
          if (!old || old === li) return;
          // The new node's attributes on the old node (state, index, qty), the old node's children.
          Array.from(old.attributes).forEach((a) => {
            if (!li.hasAttribute(a.name)) old.removeAttribute(a.name);
          });
          Array.from(li.attributes).forEach((a) => old.setAttribute(a.name, a.value));
          const ob = old.querySelector('.inv-slot__btn');
          const nb = li.querySelector('.inv-slot__btn');
          if (ob && nb) {
            Array.from(nb.attributes).forEach((a) => ob.setAttribute(a.name, a.value));
            if (ob.innerHTML !== nb.innerHTML) {
              const oi = ob.querySelector('.inv-slot__art');
              const ni = nb.querySelector('.inv-slot__art');
              // Same art: keep the decoded image, refresh the rest (the stack count).
              if (oi && ni && oi.getAttribute('src') === ni.getAttribute('src')) {
                ni.replaceWith(oi);
                ob.replaceChildren(...nb.childNodes);
              } else ob.innerHTML = nb.innerHTML;
            }
          }
          const ox = old.querySelector('[data-inv-remove]');
          const nx = li.querySelector('[data-inv-remove]');
          if (ox && nx) ox.setAttribute('aria-label', nx.getAttribute('aria-label') || '');
          else if (ox && !nx) ox.remove();
          else if (!ox && nx) old.appendChild(nx);
          li.replaceWith(old);
        });
      }
      if (body) body.scrollTop = scrollTop;
      if (body && anchorTop != null) {
        const again = el.querySelector(anchorSel);
        if (again) body.scrollTop += again.getBoundingClientRect().top - anchorTop;
      }

      this.mountPack();
      if (this.armWallets) this.armWallets();
      this.fitWallets();
      this.fitLayout(); // also when there are no wallets (under the minimum), where fitWallets returns early
      if (this.undo) this.showNotice(this.undo.text, { undo: true });

      const freshCodePanel = el.querySelector('[data-cart-code]');
      if (freshCodePanel) freshCodePanel.open = Boolean(codeWasOpen || this.codeMsg);
      if (codeState && codeState.value) {
        const freshCode = el.querySelector('[data-cart-code-input]');
        if (freshCode) {
          freshCode.value = codeState.value;
          if (codeState.focused) {
            freshCode.focus({ preventScroll: true });
            const n = freshCode.value.length;
            try {
              freshCode.setSelectionRange(n, n);
            } catch (e) {}
          }
        }
      }
      // A code box message outlives the render that follows a rejected code.
      if (this.codeMsg) {
        const errBox = el.querySelector('[data-cart-code-error]');
        if (errBox) {
          errBox.textContent = this.codeMsg;
          errBox.hidden = false;
        }
      }

      // Meter: animate from the previous value.
      const meterEl = el.querySelector('#cart-drawer-meter');
      if (meterEl && window.SourMeter) {
        const target = Number(meterEl.dataset.value) || 0;
        if (oldMeter > 0 && target > 0 && oldMeter !== target) {
          meterEl.dataset.value = String(oldMeter);
          meterEl.style.setProperty('--meter-value', oldMeter);
        }
        window.SourMeter.mountAll(el);
        const m = window.SourMeter.get(meterEl);
        if (m && target > 0) m.setValue(target, { animate: !o.instant });
      }

      // Free-shipping bar: ease from the previous fill (the fresh markup
      // carries the target), and spark once when the cart first crosses
      // the line.
      const bar = el.querySelector('[data-ship-bar]');
      if (bar && !o.instant) {
        const target = bar.style.getPropertyValue('--fill');
        if (oldFill != null && oldFill !== '' && oldFill + '%' !== target) {
          // Old value, forced style flush, new value: the transition runs
          // between the two computed styles (no rAF, which a background
          // tab never fires).
          bar.style.setProperty('--fill', oldFill + '%');
          void bar.offsetWidth;
          bar.style.setProperty('--fill', target);
        }
        if (!wasFree && el.dataset.shipsFree === 'true') bar.classList.add('is-celebrate');
      }

      if (focusKey && focusKey.key) {
        const sel =
          focusKey.action === 'slot'
            ? '[data-cart-pack] [data-inv-slot][data-key="' + focusKey.key + '"][data-index="' + focusKey.index + '"] [data-inv-btn], [data-cart-pack] [data-inv-slot][data-key="' + focusKey.key + '"] [data-inv-btn]'
            : focusKey.action === 'remove' ? '[data-cart-remove][data-key="' + focusKey.key + '"]' : focusKey.action === 'sourup' ? '[data-cart-sourup][data-variant="' + focusKey.key + '"]' : focusKey.action && focusKey.action.indexOf('qty') === 0 ? '[data-cart-qty="' + focusKey.action.slice(3) + '"][data-key="' + focusKey.key + '"]' : null;
        const next = sel && el.querySelector(sel);
        if (next) next.focus({ preventScroll: true });
        else if (this.isOpen) el.querySelector('.cart-drawer__panel').focus({ preventScroll: true });
      }

      try {
        if (!keptDynamic && window.Shopify && window.Shopify.PaymentButton && typeof window.Shopify.PaymentButton.init === 'function') window.Shopify.PaymentButton.init();
      } catch (e) {}
      setBadge(Number(el.dataset.itemCount) || 0);
    }

    /* ---------- the backpack (assets/inventory.js) ---------- */

    /** Wire the drawer's grid: slots are numbered in DOM order, then the
     *  inventory module owns tap (the card), hold / right-click (the menu). */
    /** The bags in the builder's arrangement (its saved slots — variant ids in
     *  order, assets/box-builder.js) with the ladder's free bags first, so the
     *  two backpacks show the same bags in the same places (Caleb,
     *  2026-09-15). Bags the builder does not know keep the cart's order,
     *  after the rest. The bag slots are one run at the start of the grid. */
    orderLikeBuilder(grid) {
      let saved = null;
      try {
        saved = JSON.parse(localStorage.getItem('sb:box:v1') || 'null');
      } catch (e) {
        saved = null;
      }
      const want = saved && Array.isArray(saved.slots) ? saved.slots.map(String) : [];
      const slots = Array.from(grid.children).filter((li) => li.matches('[data-inv-slot][data-key][data-kind="bag"]'));
      if (slots.length < 2) return;
      const pool = slots.slice();
      const take = (pred) => {
        const i = pool.findIndex(pred);
        return i < 0 ? null : pool.splice(i, 1)[0];
      };
      const order = [];
      // The free bags first, in the builder's order; then any free bag the
      // builder did not expect; then the rest in the builder's order.
      want.forEach((vid) => {
        const li = take((x) => x.dataset.variant === vid && x.classList.contains('is-free'));
        if (li) order.push(li);
      });
      slots.forEach((li) => {
        if (li.classList.contains('is-free') && pool.indexOf(li) >= 0) order.push(take((x) => x === li));
      });
      want.forEach((vid) => {
        const li = take((x) => x.dataset.variant === vid);
        if (li) order.push(li);
      });
      order.push(...pool);
      if (order.every((li, i) => li === slots[i])) return;
      const after = slots[slots.length - 1].nextSibling;
      order.forEach((li) => grid.insertBefore(li, after));
    }
    mountPack() {
      const el = root();
      const inv = window.SourInventory;
      const grid = el && el.querySelector('[data-cart-pack]');
      if (!inv || !grid) return;
      this.orderLikeBuilder(grid);
      Array.from(grid.children).forEach((li, i) => (li.dataset.index = String(i)));
      // A mouse can drag a bag off the grid to take it out (the RS "drop"); a
      // thumb's swipe must keep scrolling the drawer, so touch gets no drag.
      const fine = window.matchMedia && window.matchMedia('(pointer: fine)').matches;
      inv.mount(grid, {
        drag: fine ? 'out' : 'none',
        menuTitle: grid.dataset.menuTitle || 'Choose option',
        card: (slot) => this.slotCard(slot),
        menuItems: (slot) => this.slotMenu(slot),
        onRemove: (slot) => this.removeSlot(slot),
        onDragOut: (i, slot) => this.removeSlot(slot),
        onEmptyTap: () => {
          // An empty slot: the "Add N" pill fills the box; otherwise back to the shop.
          const fill = el.querySelector('[data-cart-fill-n]');
          if (fill) fill.click();
          else if (isPage()) window.location.href = el.dataset.shopUrl || ROOT_PATH + 'collections/all';
          else this.close();
        },
      });
    }
    /** The line a slot belongs to, re-resolved by key (the grid may have been re-rendered). */
    slotLine(slot) {
      const el = root();
      const key = slot && slot.getAttribute('data-key');
      return key && el ? el.querySelector('[data-cart-pack] [data-cart-line][data-key="' + key + '"]') : null;
    }
    /** Mark every slot of a line pending (a bag's units share the key). */
    markPending(key, removing) {
      const el = root();
      if (!el) return;
      el.querySelectorAll('[data-cart-pack] [data-cart-line][data-key="' + key + '"]').forEach((li) => {
        li.classList.add('is-pending');
        if (removing) li.classList.add('is-removing');
      });
    }
    /** One unit out (a bag slot), or the whole line for a stack. */
    removeSlot(slot) {
      const line = this.slotLine(slot);
      if (!line) return;
      const key = line.dataset.key;
      const qty = Number(line.dataset.qty) || 0;
      const unit = line.dataset.kind === 'bag';
      const next = unit ? Math.max(0, qty - 1) : 0;
      clearTimeout(this.pending.get(key));
      this.pending.delete(key);
      if (unit && next > 0) slot.classList.add('is-pending', 'is-removing');
      else this.markPending(key, true);
      // Optimistic: once the fade has run, the leaving slot(s) come out of the
      // grid and an empty cell keeps the row whole, so the box closes up at
      // once instead of waiting a round trip for the server's markup.
      const el = root();
      setTimeout(() => {
        const grid = el && el.querySelector('[data-cart-pack]');
        if (!grid) return;
        const gone = unit && next > 0 ? [slot] : Array.from(grid.querySelectorAll('[data-cart-line][data-key="' + key + '"]'));
        const inv = window.SourInventory;
        gone.forEach((li) => {
          if (!li.isConnected || !li.classList.contains('is-removing')) return;
          li.remove();
          grid.insertAdjacentHTML('beforeend', inv ? inv.slotHTML({ index: grid.children.length, label: grid.dataset.labelEmpty || '' }) : '<li class="inv-slot inv-slot--empty" data-inv-slot><button type="button" class="inv-slot__btn" data-inv-btn></button></li>');
        });
        Array.from(grid.children).forEach((li, i) => (li.dataset.index = String(i)));
      }, 220);
      this.change(key, next, { source: 'drawer' }).catch(() => {});
    }
    slotMenu(slot) {
      const line = this.slotLine(slot);
      if (!line) return [];
      const el = root();
      const grid = el.querySelector('[data-cart-pack]');
      const d = grid ? grid.dataset : {};
      const kind = line.dataset.kind;
      const key = line.dataset.key;
      const btn = slot.querySelector('[data-inv-btn]');
      const examine = btn ? btn.dataset.examine : '';
      const chat = el.querySelector('[data-inv-chat]');
      const items = [{ label: d.labelRemove || 'Remove', primary: true, run: () => this.removeSlot(slot) }];
      if (kind !== 'box') {
        if (kind !== 'bag') items.push({ label: d.labelMinus || 'Take one out', run: () => this.bumpQty(key, -1) });
        items.push({ label: d.labelPlus || 'Add one more', run: () => this.bumpQty(key, 1) });
      }
      items.push({ label: d.labelExamine || 'Examine', run: () => window.SourInventory.say(chat, examine) });
      if (line.dataset.url) items.push({ label: d.labelView || 'View bag', run: () => (window.location.href = line.dataset.url) });
      return items;
    }
    slotCard(slot) {
      const line = this.slotLine(slot);
      if (!line) return null;
      const el = root();
      const grid = el.querySelector('[data-cart-pack]');
      const d = grid ? grid.dataset : {};
      const kind = line.dataset.kind;
      const key = line.dataset.key;
      const qty = Number(line.dataset.qty) || 0;
      const level = Number(line.dataset.level) || 0;
      const zone = level > 0 && window.SourMeter ? window.SourMeter.zoneFor(level) : null;
      const rarity = String(line.dataset.rarity || '').trim();
      const btn = slot.querySelector('[data-inv-btn]');
      const esc = window.SourInventory.esc;
      let priceHTML = esc(line.dataset.price || '');
      if (line.dataset.compare) priceHTML += ' <s>' + esc(line.dataset.compare) + '</s>';
      const actions = [{ label: d.labelRemove || 'Remove', primary: true, run: () => this.removeSlot(slot) }];
      if (kind !== 'box') {
        if (kind !== 'bag') actions.push({ label: d.labelMinus || 'Take one out', run: () => this.bumpQty(key, -1) });
        actions.push({ label: d.labelPlus || 'Add one more', run: () => this.bumpQty(key, 1) });
      }
      if (line.dataset.url) actions.push({ label: d.labelView || 'View bag', href: line.dataset.url, quiet: true });
      return {
        kind: kind,
        image: line.dataset.image || '',
        title: line.dataset.title || '',
        pill: kind === 'box' ? d.labelMonthly || '' : rarity,
        pillClass: rarity && rarity.toUpperCase() !== 'LIMITED' ? 'sb-pill--gold' : '',
        priceHTML: priceHTML,
        priceNote: kind === 'bag' && qty > 1 ? String(d.labelInBox || '[n] in your box').replace('[n]', qty) : kind === 'box' ? line.dataset.plan || '' : '',
        level: level,
        levelText: String(d.labelLevel || 'Sour level [level]').replace('[level]', level),
        zoneName: zone ? zone.name : '',
        zoneColor: zone ? 'var(--zone-' + zone.key + ', currentColor)' : '',
        faceBase: d.face,
        examine: btn ? btn.dataset.examine : '',
        closeLabel: d.labelClose || 'Close',
        actions: actions,
      };
    }

    showNotice(text, opts) {
      const el = root();
      const notice = el && el.querySelector('[data-cart-notice]');
      if (!notice) return;
      const o = opts || {};
      notice.replaceChildren();
      const span = document.createElement('span');
      span.textContent = text;
      notice.appendChild(span);
      if (o.undo) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'cart-drawer__undo';
        b.setAttribute('data-cart-undo', '');
        b.textContent = (el.dataset.undoLabel || 'Undo');
        notice.appendChild(b);
      }
      notice.classList.toggle('is-error', !!o.error);
      notice.hidden = false;
      if (o.timeout) setTimeout(() => (notice.hidden = true), o.timeout);
    }
    hideNotice() {
      const el = root();
      const notice = el && el.querySelector('[data-cart-notice]');
      if (notice) notice.hidden = true;
    }

    /* ---------- requests ---------- */

    enqueue(fn) {
      const run = () => fn();
      this.queue = this.queue.then(run, run);
      return this.queue;
    }

    async request(url, body, meta) {
      const m = meta || {};
      const seq = ++this.seq;
      const payload = Object.assign({}, body || {}, { sections: SECTION, sections_url: ROOT_PATH });
      const res = await fetch(ROOT_PATH + url, { method: 'POST', credentials: 'same-origin', headers: jsonHeaders, body: JSON.stringify(payload), signal: timeoutSignal() });
      let json = null;
      try {
        json = await res.json();
      } catch (e) {
        json = null;
      }
      if (!res.ok) {
        const msg = (json && (json.description || json.message)) || (root() && root().dataset.errorLabel) || 'Something went wrong.';
        this.showNotice(msg, { error: true, timeout: 6000 });
        emit('cart:error', { action: m.action, source: m.source, status: res.status, description: msg });
        if (window.console && console.warn) console.warn('[cart] ' + url + ' ' + res.status, json);
        // Re-sync the visible cart after a failed edit. Straight away, not via
        // enqueue(): this runs inside a queued task, and queueing another one
        // from here waits on itself forever (every later click then hangs).
        await this.refreshNow().catch(() => {});
        const err = new Error(msg);
        err.status = res.status;
        throw err;
      }
      if (seq !== this.seq && m.allowStale !== true) {
        // A newer request already landed; don't paint an older cart over it.
        return json;
      }
      if (json && json.sections && json.sections[SECTION]) this.render(json.sections[SECTION]);
      // add.js returns the added lines, not the cart; update.js / change.js return the cart.
      if (json && typeof json.item_count === 'number') this.cart = json;
      else this.cart = await fetchCart();
      setBadge(this.cart.item_count);
      emit('cart:updated', { cart: this.cart, source: m.source || 'drawer', action: m.action });
      return json;
    }

    /** Fetch + render without touching the queue (for use inside queued work). */
    async refreshNow() {
      const [cart, html] = await Promise.all([fetchCart(), fetchSection().catch(() => null)]);
      this.cart = cart;
      if (html) this.render(html, { instant: true });
      setBadge(cart.item_count);
      return cart;
    }

    refresh(opts) {
      const o = opts || {};
      return this.enqueue(async () => {
        const [cart, html] = await Promise.all([fetchCart(), fetchSection().catch(() => null)]);
        this.cart = cart;
        if (html) this.render(html, { instant: o.quiet });
        setBadge(cart.item_count);
        if (!o.quiet) emit('cart:updated', { cart: cart, source: o.source || 'refresh', action: 'refresh' });
        return cart;
      });
    }

    add(items, opts) {
      const o = opts || {};
      let body;
      if (items instanceof HTMLFormElement) items = new FormData(items);
      if (items instanceof FormData) {
        items.delete('return_to');
        items.delete('sections');
        const obj = {};
        items.forEach((v, k) => {
          // properties[Recipient email] and friends (the gift card form) go under `properties` in the JSON body.
          const prop = /^properties\[(.+)\]$/.exec(k);
          if (prop) (obj.properties = obj.properties || {})[prop[1]] = v;
          else obj[k] = v;
        });
        body = obj;
      } else if (Array.isArray(items)) {
        body = { items: items };
      } else {
        body = items;
      }
      const planLines = (body.items || [body]).filter((it) => it && it.selling_plan);
      // The drawer slides in at once, showing the cart as it is with the
      // subtotal pending; the new line pops in when Shopify answers (add.js
      // takes about a second on the live store — Caleb, 2026-09-15: the lag).
      // A failed add still shows its notice in the open drawer.
      if (o.open !== false) {
        this.open({ refresh: false });
        const sub = root() && root().querySelector('[data-cart-subtotal]');
        if (sub) sub.classList.add('is-pending');
      }
      return this.enqueue(async () => {
        const json = await this.request('cart/add.js', body, { action: 'add', source: o.source || 'drawer' });
        clearSpinners();
        if (o.open !== false) this.open({ refresh: false });
        // A second Squire's Box is a second monthly subscription (Caleb: allow it,
        // but make sure they know): say how many and what that is a month.
        if (planLines.length) this.noteSubscriptions(planLines.map((it) => it.id));
        return { added: json, cart: this.cart };
      });
    }
    /** After an add: when a plan variant sits in the cart more than once, a notice
     *  with the count and the monthly total. Nothing to say for one. */
    noteSubscriptions(variants) {
      const cart = this.cart;
      if (!cart || !cart.items) return;
      variants.map(Number).forEach((id) => {
        const lines = cart.items.filter((i) => i.selling_plan_allocation && Number(i.variant_id) === id);
        const n = lines.reduce((a, i) => a + (Number(i.quantity) || 0), 0);
        if (n < 2) return;
        const cents = lines.reduce((a, i) => a + (Number(i.final_line_price) || 0), 0);
        const el = root();
        const tpl = (el && el.dataset.subsLabel) || "Heads up: that's [n] Squire's Box subscriptions. [n] boxes every month, [total] a month. Take one out if you meant one.";
        this.showNotice(tpl.replace(/\[n\]/g, n).replace('[total]', '$' + (cents / 100).toFixed(2)), { timeout: 8000 });
      });
    }

    change(key, quantity, opts) {
      const o = opts || {};
      return this.enqueue(() => this.request('cart/change.js', { id: key, quantity: quantity }, { action: 'change', source: o.source || 'drawer' }).then(() => this.cart));
    }

    update(updates, opts) {
      const o = opts || {};
      return this.enqueue(() => this.request('cart/update.js', { updates: updates }, { action: 'update', source: o.source || 'drawer' }).then(() => this.cart));
    }

    clear(opts) {
      const o = opts || {};
      return this.enqueue(async () => {
        const res = await fetch(ROOT_PATH + 'cart/clear.js', { method: 'POST', credentials: 'same-origin', headers: jsonHeaders });
        if (!res.ok) throw new Error('clear failed');
        const html = await fetchSection().catch(() => null);
        if (html) this.render(html);
        this.cart = await fetchCart();
        setBadge(this.cart.item_count);
        emit('cart:updated', { cart: this.cart, source: o.source || 'drawer', action: 'clear' });
        return this.cart;
      });
    }

    /* ---------- discount codes ----------
     * Shopify's Ajax cart takes a discount parameter on cart/update.js
     * (comma-separated for several, "" to clear). The cart that comes
     * back lists discount_codes with an applicable flag: a code that the
     * cart does not qualify for comes back applicable:false, so it is
     * dropped again and the shopper told, rather than left looking
     * applied. Everything else about the cart is re-rendered by
     * request() from the sections payload, as usual.
     */
    codesApplied() {
      const list = (this.cart && this.cart.discount_codes) || [];
      return list.filter((d) => d.applicable !== false).map((d) => String(d.code));
    }

    /** A code box message, from the section's data attributes (translated). */
    codeLabel(name, fallback) {
      const el = root();
      const box = el && el.querySelector('[data-cart-code]');
      return (box && box.dataset[name]) || fallback;
    }

    /**
     * The code box's message. Held on the instance as well as painted,
     * because every cart edit re-renders the footer from the server's
     * markup — a message written straight into the DOM is wiped by the
     * next render, which is exactly the render that follows a rejected
     * code. render() paints it back.
     */
    codeError(msg) {
      this.codeMsg = msg || '';
      const el = root();
      const box = el && el.querySelector('[data-cart-code-error]');
      if (!box) return;
      if (this.codeMsg) {
        box.textContent = this.codeMsg;
        box.hidden = false;
      } else {
        box.textContent = '';
        box.hidden = true;
      }
    }

    async sendCodes(codes, meta) {
      await this.request('cart/update.js', { discount: codes.join(',') }, meta);
      return this.cart;
    }

    applyCode(raw, opts) {
      const o = opts || {};
      const code = String(raw || '').trim();
      if (!code) return Promise.resolve(this.cart);
      return this.enqueue(async () => {
        this.codeError('');
        const before = this.codesApplied();
        if (before.some((c) => c.toLowerCase() === code.toLowerCase())) return this.cart;
        const wanted = before.concat([code]);
        try {
          await this.sendCodes(wanted, { action: 'discount', source: o.source || 'drawer' });
        } catch (e) {
          this.codeError(this.codeLabel('codeFailed', "Couldn't apply that code — try again."));
          return this.cart;
        }
        const list = (this.cart && this.cart.discount_codes) || [];
        const hit = list.find((d) => String(d.code).toLowerCase() === code.toLowerCase());
        // Shopify keeps a code it cannot honour on the cart, flagged
        // applicable:false. Left there it reads as applied. Take it back
        // off and say why.
        if (!hit || hit.applicable === false) {
          this.codeError(this.codeLabel('codeBad', "That code doesn't apply to this cart."));
          if (hit) await this.sendCodes(before, { action: 'discount', source: 'drawer' }).catch(() => {});
          return this.cart;
        }
        const input = root() && root().querySelector('[data-cart-code-input]');
        if (input) input.value = '';
        return this.cart;
      });
    }

    removeCode(raw) {
      const code = String(raw || '').trim();
      if (!code) return Promise.resolve(this.cart);
      return this.enqueue(async () => {
        this.codeError('');
        const rest = this.codesApplied().filter((c) => c.toLowerCase() !== code.toLowerCase());
        try {
          await this.sendCodes(rest, { action: 'discount', source: 'drawer' });
        } catch (e) {
          this.codeError(this.codeLabel('codeFailed', "Couldn't apply that code — try again."));
        }
        return this.cart;
      });
    }

    addRandom(btn) {
      let pool = [];
      try {
        pool = String(btn.dataset.pool || '')
          .split(',')
          .map(Number)
          .filter(Boolean);
      } catch (e) {}
      const count = Math.min(Number(btn.dataset.count) || 10, pool.length);
      if (!count) return Promise.resolve();
      for (let i = pool.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [pool[i], pool[j]] = [pool[j], pool[i]];
      }
      btn.setAttribute('aria-busy', 'true');
      return this.add(pool.slice(0, count).map((id) => ({ id: id, quantity: 1 })), { source: 'drawer-random', open: false }).finally(() => btn.removeAttribute('aria-busy'));
    }

    /**
     * The nudge's "Add N" button: N more in-stock bags, ones not in the cart
     * first, then repeats from the pool. Same pool as the empty state.
     */
    addToTier(btn) {
      const el = root();
      const need = Number(btn.dataset.count) || 0;
      let pool = [];
      try {
        pool = String((el && el.dataset.pool) || '')
          .split(',')
          .map(Number)
          .filter(Boolean);
      } catch (e) {}
      if (!need || !pool.length) return Promise.resolve();
      const inCart = new Set(((this.cart && this.cart.items) || []).map((it) => it.variant_id));
      for (let i = pool.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [pool[i], pool[j]] = [pool[j], pool[i]];
      }
      const fresh = pool.filter((id) => !inCart.has(id));
      const picks = [];
      for (let i = 0; picks.length < need; i++) {
        const src = i < fresh.length ? fresh : pool;
        picks.push(src[i < fresh.length ? i : (i - fresh.length) % pool.length]);
      }
      const counts = {};
      picks.forEach((id) => (counts[id] = (counts[id] || 0) + 1));
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      return this.add(Object.keys(counts).map((id) => ({ id: Number(id), quantity: counts[id] })), { source: 'drawer-fill', open: false }).finally(() => {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
      });
    }

    async swapToBox(variant, plan) {
      // Only the bag lines make way for the box; the snapshot for undo is
      // those lines alone, so undo never touches anything else in the cart.
      const el = root();
      const bagKeys = el ? [...el.querySelectorAll('[data-cart-line][data-bag]')].map((li) => li.dataset.key) : [];
      const items = (this.cart && this.cart.items ? this.cart.items : (await fetchCart()).items) || [];
      const snapshot = items
        .filter((it) => bagKeys.indexOf(it.key) !== -1)
        .map((it) => {
          const line = { id: it.variant_id, quantity: it.quantity };
          if (it.properties && Object.keys(it.properties).length) line.properties = it.properties;
          return line;
        });
      const updates = {};
      bagKeys.forEach((k) => (updates[k] = 0));
      await this.enqueue(async () => {
        if (bagKeys.length) await this.request('cart/update.js', { updates: updates }, { action: 'update', source: 'drawer-swap', allowStale: true });
        await this.request('cart/add.js', { items: [{ id: variant, quantity: 1, selling_plan: plan }] }, { action: 'swap', source: 'drawer-swap' });
      });
      this.undo = { items: snapshot, box: variant, text: el && el.dataset.swappedLabel ? el.dataset.swappedLabel : 'Switched to the monthly box.' };
      this.showNotice(this.undo.text, { undo: true });
      clearTimeout(this.undoTimer);
      this.undoTimer = setTimeout(() => {
        this.undo = null;
        this.hideNotice();
      }, UNDO_MS);
    }

    /** "Add the box too": the Squire's Box joins the cart beside the bags (no undo; the line has its own remove). */
    addBox(variant, plan) {
      return this.enqueue(async () => {
        const json = await this.request('cart/add.js', { items: [{ id: variant, quantity: 1, selling_plan: plan }] }, { action: 'add', source: 'drawer-addbox' });
        this.noteSubscriptions([variant]);
        return json;
      });
    }

    async undoSwap() {
      if (!this.undo) return;
      const items = this.undo.items;
      const box = this.undo.box;
      this.undo = null;
      clearTimeout(this.undoTimer);
      this.hideNotice();
      // Take the box line back out, put the bags back; leave the rest alone.
      const cart = this.cart && this.cart.items ? this.cart : await fetchCart();
      const updates = {};
      (cart.items || []).forEach((it) => {
        if (it.selling_plan_allocation && it.variant_id === box) updates[it.key] = 0;
      });
      if (Object.keys(updates).length) await this.update(updates, { source: 'drawer-undo' });
      if (items.length) await this.add(items, { source: 'drawer-undo', open: false });
    }

    /* ---------- optimistic qty edits ---------- */

    bumpQty(key, delta) {
      const el = root();
      const line = el && el.querySelector('[data-cart-line][data-key="' + key + '"]');
      if (!line) return;
      const next = Math.max(0, (Number(line.dataset.qty) || 0) + delta);
      // A bag's units each carry the line: keep every slot's count in step.
      el.querySelectorAll('[data-cart-line][data-key="' + key + '"]').forEach((li) => {
        li.dataset.qty = String(next);
        const val = li.querySelector('[data-cart-qty-value]');
        if (val) val.textContent = String(next);
        li.classList.add('is-pending');
        if (next === 0) li.classList.add('is-removing');
      });
      const sub = el.querySelector('[data-cart-subtotal]');
      if (sub) sub.classList.add('is-pending');
      clearTimeout(this.pending.get(key));
      this.pending.set(
        key,
        setTimeout(() => {
          this.pending.delete(key);
          this.change(key, next, { source: 'drawer' }).catch(() => {});
        }, 150)
      );
    }

    /* ---------- wiring ---------- */

    bind() {
      // Header icon.
      document.addEventListener('click', (e) => {
        const link = e.target.closest && e.target.closest('#header-cart');
        if (!link) return;
        e.preventDefault();
        this.toggle();
      });
      const link = document.getElementById('header-cart');
      if (link) {
        link.setAttribute('aria-controls', 'cart-drawer');
        link.setAttribute('aria-haspopup', 'dialog');
        link.setAttribute('aria-expanded', 'false');
      }

      // Every cart/add form on the site, ahead of main.js's own handlers.
      document.addEventListener(
        'submit',
        (e) => {
          const form = e.target;
          if (!(form instanceof HTMLFormElement)) return;
          if (form.matches('[data-starter-form], [data-cart-native]')) return;
          const action = form.getAttribute('action') || '';
          if (!/\/cart\/add/.test(action)) return;
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          if (form.dataset.busy) return;
          form.dataset.busy = '1';
          const btn = form.querySelector('.decorated-button, .product-form__submit, button[type="submit"]');
          if (btn) btn.setAttribute('aria-busy', 'true');
          const inQuickview = !!form.closest('#quickview');
          const source = inQuickview ? 'quickview' : form.id === 'product-form' ? 'product' : form.classList.contains('product-card__form') ? 'card' : 'form';
          this.add(form, { source: source })
            .then(() => {
              if (inQuickview) {
                const close = document.querySelector('#quickview [data-quickview-close]');
                if (close) close.click();
              }
            })
            .catch((err) => {
              if (!err || !err.status) HTMLFormElement.prototype.submit.call(form); // network failure: native fallback
            })
            .finally(() => {
              delete form.dataset.busy;
              if (btn) {
                btn.removeAttribute('aria-busy');
                btn.classList.remove('decorated-button--loading');
              }
              clearSpinners();
            });
        },
        true
      );

      // Inside the drawer.
      document.addEventListener('click', (e) => {
        const el = root();
        if (!el || !el.contains(e.target)) return;
        const t = e.target;
        const q = t.closest('[data-cart-qty]');
        if (q) {
          this.bumpQty(q.dataset.key, Number(q.dataset.cartQty));
          return;
        }
        const rm = t.closest('[data-cart-remove]');
        if (rm) {
          this.markPending(rm.dataset.key, true);
          clearTimeout(this.pending.get(rm.dataset.key));
          this.pending.delete(rm.dataset.key);
          this.change(rm.dataset.key, 0).catch(() => {});
          return;
        }
        const codeApply = t.closest('[data-cart-code-apply]');
        if (codeApply) {
          const input = el.querySelector('[data-cart-code-input]');
          if (input) this.applyCode(input.value).catch(() => {});
          return;
        }
        const codeRemove = t.closest('[data-cart-code-remove]');
        if (codeRemove) {
          this.removeCode(codeRemove.getAttribute('data-cart-code-remove')).catch(() => {});
          return;
        }
        const clearBtn = t.closest('[data-cart-clear]');
        if (clearBtn) {
          // Two taps, not one. Clear cart sits under the wallets with no
          // confirmation and no undo, and a stray tap emptied a cart
          // somebody had spent minutes filling.
          if (clearBtn.dataset.armed) {
            this.clear().catch(() => {});
          } else {
            clearBtn.dataset.armed = '1';
            const idle = clearBtn.dataset.idleLabel || clearBtn.textContent;
            clearBtn.dataset.idleLabel = idle;
            clearBtn.textContent = clearBtn.dataset.confirmLabel || 'Tap again to clear';
            clearTimeout(this.clearTimer);
            this.clearTimer = setTimeout(() => {
              if (!clearBtn.isConnected) return;
              delete clearBtn.dataset.armed;
              clearBtn.textContent = idle;
            }, 4000);
          }
          return;
        }
        const su = t.closest('[data-cart-sourup]');
        if (su) {
          this.anchor = '[data-cart-strip]';
          if (su.getAttribute('aria-pressed') === 'true') {
            const line = el.querySelector('[data-cart-line][data-variant="' + su.dataset.variant + '"]');
            if (line) this.change(line.dataset.key, 0).catch(() => {});
          } else {
            su.setAttribute('aria-pressed', 'true');
            this.add([{ id: Number(su.dataset.variant), quantity: 1 }], { source: 'drawer-sourup', open: false }).catch(() => su.setAttribute('aria-pressed', 'false'));
          }
          return;
        }
        const tierBtn = t.closest('[data-cart-fill-n]');
        if (tierBtn) {
          this.addToTier(tierBtn).catch(() => {});
          return;
        }
        const addBtn = t.closest('[data-cart-add][data-variant]');
        if (addBtn) {
          addBtn.disabled = true;
          this.add([{ id: Number(addBtn.dataset.variant), quantity: 1 }], { source: 'drawer-fill', open: false }).catch(() => (addBtn.disabled = false));
          return;
        }
        const addBox = t.closest('[data-cart-addbox]');
        if (addBox) {
          addBox.setAttribute('aria-busy', 'true');
          this.addBox(Number(addBox.dataset.variant), Number(addBox.dataset.plan))
            .catch(() => {})
            .finally(() => addBox.removeAttribute('aria-busy'));
          return;
        }
        const swap = t.closest('[data-cart-swap]');
        if (swap) {
          swap.setAttribute('aria-busy', 'true');
          this.swapToBox(Number(swap.dataset.variant), Number(swap.dataset.plan))
            .catch(() => {})
            .finally(() => swap.removeAttribute('aria-busy'));
          return;
        }
        if (t.closest('[data-cart-undo]')) {
          this.undoSwap().catch(() => {});
          return;
        }
        const rnd = t.closest('[data-cart-random]');
        if (rnd) {
          this.addRandom(rnd).catch(() => {});
          return;
        }
        const chk = t.closest('[data-cart-checkout]');
        if (chk) {
          chk.setAttribute('aria-busy', 'true'); // native submit of {% form 'cart' %} → /checkout
          return;
        }
        if (t.closest('[data-cart-close]') && !isPage()) {
          // On the page a "close" is a plain link (Continue shopping): let it navigate.
          e.preventDefault();
          this.close();
        }
      });

      // Enter in the code box applies the code. Without this it submits
      // the cart form, which goes straight to checkout — the code typed
      // and the cart gone in one keypress. Bound before the drawer's own
      // Escape/Tab trap so it also runs on the /cart page.
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        const input = e.target && e.target.closest && e.target.closest('[data-cart-code-input]');
        if (!input) return;
        e.preventDefault();
        this.applyCode(input.value).catch(() => {});
      });

      document.addEventListener('keydown', (e) => {
        if (!this.isOpen || isPage()) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          this.close();
          return;
        }
        if (e.key === 'Tab') {
          const el = root();
          const items = [...el.querySelectorAll(FOCUSABLE)].filter((n) => n.offsetParent !== null);
          if (!items.length) return;
          const first = items[0];
          const last = items[items.length - 1];
          if (e.shiftKey && (document.activeElement === first || !el.contains(document.activeElement))) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      });

      document.addEventListener('shopify:section:load', (e) => {
        if (e.target && (e.target.id === 'shopify-section-' + SECTION || e.target.querySelector('#cart-drawer'))) this.mountPack();
      });
    }

    installShims() {
      window.monster_refresh = () => this.refresh({ source: 'legacy', quiet: true });
      window.MonsterCart = { open: () => this.open() };
      document.addEventListener('monster:cart:open', () => this.open());
    }
  }

  const drawer = new Drawer();
  window.SourCart = drawer;
  window.addEventListener('pageshow', (e) => {
    if (e.persisted && window.SourCart) window.SourCart.refresh({ source: 'pageshow', quiet: true });
  });
  // Populate the badge from the live cart once (server-rendered value may be stale after a back-nav).
  fetchCart()
    .then((c) => {
      drawer.cart = c;
      setBadge(c.item_count);
    })
    .catch(() => {});
})();
